import { useState, useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useStreamingQuery } from '@/hooks/useStreamingQuery';
import { LoginArea } from '@/components/auth/LoginArea';
import { MagazineCard } from '@/components/MagazineCard';
import { EbookCard } from '@/components/EbookCard';
import { CreateMagazineDialog } from '@/components/CreateMagazineDialog';
import { UploadIssueDialog } from '@/components/UploadIssueDialog';
import { UploadEbookDialog } from '@/components/UploadEbookDialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BookOpen, BookMarked, Plus, Search, BookText, ExternalLink, ChevronDown, Settings, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { KIND_MAGAZINE, KIND_EBOOK } from '@/lib/pressstrKinds';
import type { NostrEvent } from '@nostrify/nostrify';

function MagazineGridSkeleton() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="aspect-[2/3] rounded-lg w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-3 w-3/4" />
        </div>
      ))}
    </div>
  );
}

export default function Index() {
  const { user } = useCurrentUser();

  const [search, setSearch] = useState('');
  const [createMagazineOpen, setCreateMagazineOpen] = useState(false);
  const [uploadIssueOpen, setUploadIssueOpen] = useState(false);
  const [uploadEbookOpen, setUploadEbookOpen] = useState(false);

  useSeoMeta({
    title: 'PressStr — Magazines & Ebooks on Nostr',
    description: 'Discover and publish magazines and ebooks on the decentralized Nostr network.',
  });

  const magazineFilters = useMemo(() => [{ kinds: [KIND_MAGAZINE], limit: 200 }], []);
  const ebookFilters    = useMemo(() => [{ kinds: [KIND_EBOOK],    limit: 200 }], []);

  const { events: magazines, isLoading: magazinesLoading, isStreaming: magazinesStreaming } =
    useStreamingQuery(magazineFilters, ['magazines']);
  const { events: ebooks, isLoading: ebooksLoading, isStreaming: ebooksStreaming } =
    useStreamingQuery(ebookFilters, ['ebooks']);

  const filteredMagazines = magazines.filter((e: NostrEvent) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    const title = e.tags.find(([n]) => n === 'title')?.[1]?.toLowerCase() ?? '';
    const summary = e.tags.find(([n]) => n === 'summary')?.[1]?.toLowerCase() ?? '';
    const topics = e.tags.filter(([n]) => n === 't').map(([, v]) => v.toLowerCase()).join(' ');
    return title.includes(s) || summary.includes(s) || topics.includes(s);
  });

  const filteredEbooks = ebooks.filter((e: NostrEvent) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    const title = e.tags.find(([n]) => n === 'title')?.[1]?.toLowerCase() ?? '';
    const summary = e.tags.find(([n]) => n === 'summary')?.[1]?.toLowerCase() ?? '';
    const authors = e.tags.filter(([n]) => n === 'author').map(([, v]) => v.toLowerCase()).join(' ');
    const topics = e.tags.filter(([n]) => n === 't').map(([, v]) => v.toLowerCase()).join(' ');
    return title.includes(s) || summary.includes(s) || authors.includes(s) || topics.includes(s);
  });

  // No-op kept for dialog onSuccess callbacks — streaming auto-includes new events
  const invalidateAll = () => {};

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-3 sm:px-4 h-14 flex items-center gap-2 sm:gap-3">
          {/* Logo */}
          <div className="flex items-center gap-1.5 shrink-0">
            <BookText className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            <span className="font-bold text-base sm:text-lg">PressStr</span>
          </div>

          {/* Search — takes remaining space */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="pl-8 h-9 text-sm"
            />
          </div>

          {/* Publish menu (logged in) */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="shrink-0 gap-1 px-2 sm:px-3">
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Publish</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setCreateMagazineOpen(true)}>
                  <BookOpen className="w-4 h-4 mr-2" />New Magazine
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setUploadIssueOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />Upload Issue
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setUploadEbookOpen(true)}>
                  <BookMarked className="w-4 h-4 mr-2" />Upload Ebook
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button variant="ghost" size="icon" className="shrink-0 size-9" asChild>
            <Link to="/settings">
              <Settings className="w-4 h-4" />
            </Link>
          </Button>

          <LoginArea className="shrink-0 max-w-[9rem] sm:max-w-48" />
        </div>
      </header>

      {/* Hero — compact on mobile */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-primary/5 border-b">
        <div className="container mx-auto px-4 py-8 sm:py-12 text-center">
          <div className="inline-flex items-center gap-2 mb-3 sm:mb-4 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-medium">
            <BookText className="w-3.5 h-3.5" />
            Decentralized Publishing on Nostr
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 tracking-tight">
            Magazines &amp; Ebooks
            <span className="text-primary block sm:inline"> on Nostr</span>
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base lg:text-lg max-w-2xl mx-auto mb-6 sm:mb-8">
            Censorship-resistant publishing. Read, share, and distribute — owned by you, forever.
          </p>
          {user ? (
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
              <Button size="default" onClick={() => setCreateMagazineOpen(true)}>
                <Plus className="w-4 h-4 mr-1.5" />New Magazine
              </Button>
              <Button size="default" variant="outline" onClick={() => setUploadEbookOpen(true)}>
                <BookMarked className="w-4 h-4 mr-1.5" />Upload Ebook
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <p className="text-sm text-muted-foreground">Log in with your Nostr identity to publish</p>
              <LoginArea className="max-w-xs" />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <Tabs defaultValue="magazines">
          <TabsList className="mb-5 sm:mb-6 w-full sm:w-auto">
            <TabsTrigger value="magazines" className="flex-1 sm:flex-none gap-1.5">
              <BookOpen className="w-4 h-4" />
              Magazines
              {magazinesStreaming
                ? <Loader2 className="w-3 h-3 animate-spin text-muted-foreground hidden sm:inline-block" />
                : <span className="text-xs text-muted-foreground hidden sm:inline">({filteredMagazines.length})</span>
              }
            </TabsTrigger>
            <TabsTrigger value="ebooks" className="flex-1 sm:flex-none gap-1.5">
              <BookMarked className="w-4 h-4" />
              Ebooks
              {ebooksStreaming
                ? <Loader2 className="w-3 h-3 animate-spin text-muted-foreground hidden sm:inline-block" />
                : <span className="text-xs text-muted-foreground hidden sm:inline">({filteredEbooks.length})</span>
              }
            </TabsTrigger>
          </TabsList>

          <TabsContent value="magazines">
            {magazinesLoading ? (
              <MagazineGridSkeleton />
            ) : filteredMagazines.length === 0 && !magazinesStreaming ? (
              <Card className="border-dashed">
                <CardContent className="py-16 text-center">
                  <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground max-w-sm mx-auto">
                    {search ? `No magazines found for "${search}".` : 'No magazines yet. Be the first to publish!'}
                  </p>
                  {user && !search && (
                    <Button className="mt-4" onClick={() => setCreateMagazineOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />Create Magazine
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
                {filteredMagazines.map((e: NostrEvent) => (
                  <MagazineCard key={e.id} event={e} onDeleted={invalidateAll} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="ebooks">
            {ebooksLoading ? (
              <MagazineGridSkeleton />
            ) : filteredEbooks.length === 0 && !ebooksStreaming ? (
              <Card className="border-dashed">
                <CardContent className="py-16 text-center">
                  <BookMarked className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground max-w-sm mx-auto">
                    {search ? `No ebooks found for "${search}".` : 'No ebooks yet. Share one now!'}
                  </p>
                  {user && !search && (
                    <Button className="mt-4" onClick={() => setUploadEbookOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />Upload Ebook
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
                {filteredEbooks.map((e: NostrEvent) => (
                  <EbookCard key={e.id} event={e} onDeleted={invalidateAll} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t py-5 mt-6">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground space-y-1">
          <p>
            <a href="https://marlowe.shakespeare.wtf" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors inline-flex items-center gap-1">
              Vibed with Marlowe <ExternalLink className="w-3 h-3" />
            </a>
          </p>
          <p className="text-xs">PressStr v2.0.0 · Built on Nostr · Files hosted on Blossom</p>
        </div>
      </footer>

      {/* Dialogs */}
      <CreateMagazineDialog
        open={createMagazineOpen}
        onOpenChange={setCreateMagazineOpen}
        onSuccess={invalidateAll}
      />
      <UploadIssueDialog
        open={uploadIssueOpen}
        onOpenChange={setUploadIssueOpen}
        onSuccess={invalidateAll}
      />
      <UploadEbookDialog
        open={uploadEbookOpen}
        onOpenChange={setUploadEbookOpen}
        onSuccess={invalidateAll}
      />
    </div>
  );
}
